import type {
  BlogAuthorDownloadable,
  BlogAuthorRecord,
  BlogAuthorVideo,
} from "~/lib/blog-author-types";

export type AuthorMediaType =
  | "Video"
  | "Webinar"
  | "Podcast"
  | "White Paper"
  | "eBook"
  | "Newsletter";

export type RecentMediaIconType =
  | "video"
  | "webinar"
  | "podcast"
  | "whitepaper"
  | "ebook"
  | "newsletter";

export type RecentMediaSegmentOpt = {
  link_label: string;
  file_name: string;
  resource_type: string;
  resource_author: string;
  is_gated_asset: "true" | "false";
  location: "body";
  resource_interaction: string;
};

export type RecentMediaItem = {
  id: string;
  text: string;
  buttonText: string;
  href: string;
  iconType: RecentMediaIconType;
  sortDate: number;
  segmentOpt: RecentMediaSegmentOpt;
};

const ICONS: Record<RecentMediaIconType, string> = {
  video: "/images/icons/resources/author-media/video.png",
  webinar: "/images/icons/resources/author-media/webinar.png",
  podcast: "/images/icons/resources/author-media/podcast.png",
  whitepaper: "/images/icons/resources/author-media/whitepaper.png",
  ebook: "/images/icons/resources/author-media/ebook.png",
  newsletter: "/images/icons/resources/author-media/newsletter.png",
};

const MEDIA_TYPE_META: Record<
  AuthorMediaType,
  {
    iconType: RecentMediaIconType;
    ctaKind: "watch" | "listen" | "read";
    resourceType: string;
    resourceInteraction: string;
    defaultButtonText: string;
  }
> = {
  Video: {
    iconType: "video",
    ctaKind: "watch",
    resourceType: "webinar/video",
    resourceInteraction: "view a video",
    defaultButtonText: "Watch",
  },
  Webinar: {
    iconType: "webinar",
    ctaKind: "watch",
    resourceType: "webinar/video",
    resourceInteraction: "view a video",
    defaultButtonText: "Watch",
  },
  Podcast: {
    iconType: "podcast",
    ctaKind: "listen",
    resourceType: "podcast",
    resourceInteraction: "go to podcast page",
    defaultButtonText: "Listen",
  },
  "White Paper": {
    iconType: "whitepaper",
    ctaKind: "read",
    resourceType: "white paper",
    resourceInteraction: "read a PDF",
    defaultButtonText: "Read",
  },
  eBook: {
    iconType: "ebook",
    ctaKind: "read",
    resourceType: "ebook",
    resourceInteraction: "read a PDF",
    defaultButtonText: "Read",
  },
  Newsletter: {
    iconType: "newsletter",
    ctaKind: "read",
    resourceType: "newsletter",
    resourceInteraction: "view newsletter subscription form",
    defaultButtonText: "Read",
  },
};

export const getRecentMediaIconSrc = (iconType: RecentMediaIconType) =>
  ICONS[iconType];

const toTimestamp = (value?: string | null) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const getDownloadableHref = (downloadable: {
  fileType?: string | null;
  cta?: { url?: string | null } | null;
  file?: { url?: string | null }[] | null;
}) => {
  if (downloadable.fileType === "Gated Asset") {
    return downloadable.cta?.url || "#";
  }

  return downloadable.file?.[0]?.url || downloadable.cta?.url || "#";
};

const getLinkLabel = (kind: "watch" | "listen" | "read") => {
  if (kind === "watch") return "Watch";
  if (kind === "listen") return "Listen";
  return "Read";
};

const getDownloadableFileName = (downloadable: {
  header?: string | null;
  fileType?: string | null;
  file?: { url?: string | null }[] | null;
}) => {
  if (downloadable.fileType?.toLowerCase() === "ebook") {
    return downloadable.header || "";
  }

  return (
    downloadable.file?.[0]?.url?.split("/").pop() || downloadable.header || ""
  );
};

const getDownloadableResourceInteraction = ({
  mediaType,
  href,
  isGatedAsset,
}: {
  mediaType: "White Paper" | "eBook";
  href: string;
  isGatedAsset: boolean;
}) => {
  if (mediaType === "eBook") {
    return href.startsWith("/") ? "view the resource page" : "read a PDF";
  }

  if (isGatedAsset || href.startsWith("/")) {
    return "view the white paper in web";
  }

  return "read a PDF";
};

const buildSegmentOpt = ({
  linkLabel,
  fileName,
  resourceType,
  resourceAuthor,
  isGatedAsset,
  resourceInteraction,
}: {
  linkLabel: string;
  fileName: string;
  resourceType: string;
  resourceAuthor: string;
  isGatedAsset: boolean;
  resourceInteraction: string;
}): RecentMediaSegmentOpt => ({
  link_label: linkLabel,
  file_name: fileName,
  resource_type: resourceType,
  resource_author: resourceAuthor,
  is_gated_asset: isGatedAsset ? "true" : "false",
  location: "body",
  resource_interaction: resourceInteraction,
});

const buildItem = ({
  id,
  text,
  buttonText,
  href,
  iconType,
  sortDate,
  segmentOpt,
}: RecentMediaItem): RecentMediaItem => ({
  id,
  text,
  buttonText,
  href,
  iconType,
  sortDate,
  segmentOpt,
});

const buildVideoItem = (
  mediaType: "Video" | "Webinar",
  video: {
    id?: number | string;
    documentId?: string;
    title?: string | null;
    description?: string | null;
    videoLink?: string | null;
    publishDate?: string | null;
    publishedAt?: string | null;
  },
  resourceAuthor: string,
  index: number,
): RecentMediaItem | null => {
  if (!video?.videoLink) return null;

  const text = video.title || video.description;
  if (!text) return null;

  const meta = MEDIA_TYPE_META[mediaType];

  return buildItem({
    id: `${mediaType}-${video.documentId || video.id || index}`,
    text,
    buttonText: meta.defaultButtonText,
    href: video.videoLink,
    iconType: meta.iconType,
    sortDate:
      toTimestamp(video.publishDate) || toTimestamp(video.publishedAt) || 0,
    segmentOpt: buildSegmentOpt({
      linkLabel: getLinkLabel(meta.ctaKind),
      fileName: video.title || text,
      resourceType: meta.resourceType,
      resourceAuthor,
      isGatedAsset: false,
      resourceInteraction: meta.resourceInteraction,
    }),
  });
};

const buildPodcastItem = (
  podcast: {
    id?: number | string;
    documentId?: string;
    title?: string | null;
    subtitle?: string | null;
    publishDate?: string | null;
    publishedAt?: string | null;
    cta?: { url?: string | null; cta?: string | null } | null;
  },
  resourceAuthor: string,
  index: number,
): RecentMediaItem | null => {
  if (!podcast?.cta?.url) return null;

  const text = podcast.title || podcast.subtitle;
  if (!text) return null;

  const meta = MEDIA_TYPE_META.Podcast;

  return buildItem({
    id: `podcast-${podcast.documentId || podcast.id || index}`,
    text,
    buttonText: meta.defaultButtonText,
    href: podcast.cta.url,
    iconType: meta.iconType,
    sortDate:
      toTimestamp(podcast.publishDate) || toTimestamp(podcast.publishedAt) || 0,
    segmentOpt: buildSegmentOpt({
      linkLabel: getLinkLabel(meta.ctaKind),
      fileName: podcast.title || text,
      resourceType: meta.resourceType,
      resourceAuthor,
      isGatedAsset: false,
      resourceInteraction: meta.resourceInteraction,
    }),
  });
};

const buildDownloadableItem = (
  mediaType: "White Paper" | "eBook",
  downloadable: {
    id?: number | string;
    documentId?: string;
    header?: string | null;
    description?: string | null;
    fileType?: string | null;
    publishDate?: string | null;
    publishedAt?: string | null;
    cta?: { url?: string | null; cta?: string | null } | null;
    file?: { url?: string | null }[] | null;
  },
  resourceAuthor: string,
  index: number,
): RecentMediaItem | null => {
  const text = downloadable?.header || downloadable?.description;
  if (!downloadable || !text) return null;

  const href = getDownloadableHref(downloadable);
  if (href === "#") return null;

  const meta = MEDIA_TYPE_META[mediaType];
  const isGatedAsset = downloadable.fileType === "Gated Asset";

  const entityId = downloadable.documentId || downloadable.id || index;
  const idPrefix = mediaType === "White Paper" ? "white-paper" : "ebook";

  return buildItem({
    id: `${idPrefix}-${entityId}`,
    text,
    buttonText: meta.defaultButtonText,
    href,
    iconType: meta.iconType,
    sortDate:
      toTimestamp(downloadable.publishDate) ||
      toTimestamp(downloadable.publishedAt) ||
      0,
    segmentOpt: buildSegmentOpt({
      linkLabel: getLinkLabel(meta.ctaKind),
      fileName: getDownloadableFileName(downloadable),
      resourceType: meta.resourceType,
      resourceAuthor,
      isGatedAsset,
      resourceInteraction: getDownloadableResourceInteraction({
        mediaType,
        href,
        isGatedAsset,
      }),
    }),
  });
};

const buildNewsletterItem = (
  newsletter: {
    id?: number | string;
    documentId?: string;
    title?: string | null;
    publishDate?: string | null;
    publishedAt?: string | null;
    cta?: { url?: string | null; cta?: string | null } | null;
  },
  resourceAuthor: string,
  index: number,
): RecentMediaItem | null => {
  if (!newsletter?.title) return null;

  const meta = MEDIA_TYPE_META.Newsletter;
  const href = newsletter.cta?.url || "/outshift-newsletter";

  return buildItem({
    id: `newsletter-${newsletter.documentId || newsletter.id || index}`,
    text: newsletter.title,
    buttonText: meta.defaultButtonText,
    href,
    iconType: meta.iconType,
    sortDate:
      toTimestamp(newsletter.publishDate) ||
      toTimestamp(newsletter.publishedAt) ||
      0,
    segmentOpt: buildSegmentOpt({
      linkLabel: getLinkLabel(meta.ctaKind),
      fileName: newsletter.title,
      resourceType: meta.resourceType,
      resourceAuthor,
      isGatedAsset: false,
      resourceInteraction: meta.resourceInteraction,
    }),
  });
};

export const buildAuthorRecentMedia = (
  author: Pick<
    BlogAuthorRecord,
    | "Name"
    | "videos"
    | "webinars"
    | "podcasts"
    | "whitePapers"
    | "eBooks"
    | "newsletters"
  >,
): RecentMediaItem[] => {
  const resourceAuthor = author.Name || "";
  const items: RecentMediaItem[] = [];
  const seenDownloadableIds = new Set<string>();
  const seenVideoIds = new Set<string>();
  const seenPodcastIds = new Set<string>();
  const seenNewsletterIds = new Set<string>();

  const getVideoEntityId = (video: BlogAuthorVideo) =>
    String(video.documentId || video.id || video.videoLink || "");

  const addVideo = (
    mediaType: "Video" | "Webinar",
    video: BlogAuthorVideo,
    index: number,
  ) => {
    const entityId = getVideoEntityId(video);
    if (entityId && seenVideoIds.has(entityId)) return;

    const item = buildVideoItem(mediaType, video, resourceAuthor, index);
    if (!item) return;

    if (entityId) seenVideoIds.add(entityId);
    items.push(item);
  };

  const addDownloadable = (
    mediaType: "White Paper" | "eBook",
    downloadable: BlogAuthorDownloadable,
    index: number,
  ) => {
    const entityId = String(
      downloadable.documentId || downloadable.id || "",
    );
    if (entityId && seenDownloadableIds.has(entityId)) return;

    const item = buildDownloadableItem(
      mediaType,
      downloadable,
      resourceAuthor,
      index,
    );
    if (!item) return;

    if (entityId) seenDownloadableIds.add(entityId);
    items.push(item);
  };

  author.videos?.forEach((video, index) => {
    addVideo("Video", video, index);
  });

  author.webinars?.forEach((video, index) => {
    addVideo("Webinar", video, index);
  });

  author.podcasts?.forEach((podcast, index) => {
    const entityId = String(
      podcast.documentId || podcast.id || podcast.cta?.url || "",
    );
    if (entityId && seenPodcastIds.has(entityId)) return;

    const item = buildPodcastItem(podcast, resourceAuthor, index);
    if (!item) return;

    if (entityId) seenPodcastIds.add(entityId);
    items.push(item);
  });

  author.whitePapers?.forEach((downloadable, index) => {
    addDownloadable("White Paper", downloadable, index);
  });

  author.eBooks?.forEach((downloadable, index) => {
    addDownloadable("eBook", downloadable, index);
  });

  author.newsletters?.forEach((newsletter, index) => {
    const entityId = String(
      newsletter.documentId || newsletter.id || newsletter.cta?.url || "",
    );
    if (entityId && seenNewsletterIds.has(entityId)) return;

    const item = buildNewsletterItem(newsletter, resourceAuthor, index);
    if (!item) return;

    if (entityId) seenNewsletterIds.add(entityId);
    items.push(item);
  });

  return items.sort((a, b) => b.sortDate - a.sortDate);
};
